/**
 * DesktopAccount — "Account & settings" screen for the Scrivar desktop shell.
 *
 * Renders inside the shell's scrollable content area (.scrivar-desktop scroll).
 * Reproduces the mockup `.view[data-view="account"]` layout:
 *   - Profile row (big avatar initials + name + email + "Edit profile" button)
 *   - Settings list card (Plan, License & devices, Cloud & AI, Language, Updates)
 *   - "Sign out" button
 *
 * Uses --ds-* design tokens defined in the shell's CSS.
 */

import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import {
  BadgeCheck, ShieldCheck, Cloud, Languages, Moon, RefreshCw, ChevronRight, LogOut,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { signOut } from '@/lib/auth';
import { useBilling } from '@/hooks/useBilling';
import { useUpdateStatus } from '@/hooks/useUpdateStatus';
import { openExternalUrl, SCRIVAR_BILLING_URL } from '@/lib/external';
import { getTrialDaysRemaining } from '@/lib/license';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import ThemeSwitcher from '@/components/ThemeSwitcher';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Generate up-to-2-letter initials from a display name or email. */
function getInitials(name) {
  if (!name) return '?';
  const trimmed = name.trim();
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  // Single word or email: take first two chars of the local part
  const local = trimmed.split('@')[0];
  return local.slice(0, 2).toUpperCase();
}

/** Plan subtitle: honest per tier, shows trial days when on trial. */
function planSubtitle(tier) {
  if (tier === 'lifetime_pro') return 'Pro Lifetime — owned';
  if (tier === 'lifetime_lite') return 'Lite Lifetime — owned';
  const trialDays = getTrialDaysRemaining();
  if (trialDays !== null && trialDays > 0) {
    return `Free trial — ${trialDays} day${trialDays === 1 ? '' : 's'} left`;
  }
  return 'Free';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ProfileRow({ name, email, onEditProfile }) {
  const initials = getInitials(name || email);

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      padding: '20px 22px',
      background: 'var(--ds-surface, #fff)',
      border: '1px solid var(--ds-border, #EAEBF0)',
      borderRadius: 'var(--ds-radius, 12px)',
      boxShadow: 'var(--ds-shadow-card, 0 1px 2px rgba(20,22,48,.05))',
    }}>
      {/* Big avatar */}
      <div style={{
        width: '56px',
        height: '56px',
        borderRadius: '14px',
        background: 'linear-gradient(140deg, var(--ds-mark-grad-1, #5B53E8), var(--ds-mark-grad-2, #8B7FF0))',
        color: 'var(--ds-content, #fff)',
        fontWeight: 600,
        fontSize: '21px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        userSelect: 'none',
      }}>
        {initials}
      </div>

      {/* Name + email */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 style={{
          fontSize: '17px',
          fontWeight: 600,
          letterSpacing: '-0.2px',
          margin: 0,
          color: 'var(--ds-ink, #191A24)',
        }}>
          {name}
        </h3>
        <p style={{
          fontSize: '13px',
          color: 'var(--ds-ink-3, #8C8FA0)',
          margin: '2px 0 0',
        }}>
          {email}
        </p>
      </div>

      {/* Edit profile button */}
      <button
        type="button"
        onClick={onEditProfile}
        style={{
          height: '36px',
          padding: '0 15px',
          borderRadius: '9px',
          background: 'var(--ds-surface, #fff)',
          border: '1px solid var(--ds-border-2, #D4D7E3)',
          fontFamily: 'inherit',
          fontSize: '12.5px',
          fontWeight: 600,
          color: 'var(--ds-ink-2, #565869)',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        Edit profile
      </button>
    </div>
  );
}

function SettingsItem({ icon: Icon, title, subtitle, value, onValueClick, onRowClick, valueColor }) {
  return (
    <div
      onClick={onRowClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '15px 20px',
        borderBottom: '1px solid var(--ds-border, #EAEBF0)',
        cursor: onRowClick ? 'pointer' : 'default',
      }}
    >
      {/* Icon container */}
      <div style={{
        width: '34px',
        height: '34px',
        borderRadius: '9px',
        background: 'var(--ds-sidebar, #F4F5F8)',
        color: 'var(--ds-ink-2, #565869)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon size={17} />
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <h5 style={{ fontSize: '13.5px', fontWeight: 600, margin: '0 0 1px', color: 'var(--ds-ink, #191A24)' }}>
          {title}
        </h5>
        <p style={{ fontSize: '12px', color: 'var(--ds-ink-3, #8C8FA0)', margin: 0 }}>
          {subtitle}
        </p>
      </div>

      {/* Value / action */}
      {value !== undefined && (
        <div
          onClick={onValueClick ? (e) => { e.stopPropagation(); onValueClick(); } : undefined}
          style={{
            fontSize: '13px',
            color: valueColor || 'var(--ds-ink-2, #565869)',
            fontWeight: 500,
            cursor: onValueClick ? 'pointer' : 'default',
          }}
        >
          {value}
        </div>
      )}

      <ChevronRight size={16} color="var(--ds-ink-4, #A9ABB8)" />
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function DesktopAccount() {
  const navigate = useNavigate();
  const { user, account } = useAuth();
  const { tier, addonSubscription, hasLifetime } = useBilling();
  const { version } = useUpdateStatus();

  const name = user?.displayName || account?.email || 'Your account';
  const email = user?.email || account?.email || '';

  async function handleSignOut() {
    await signOut();
    navigate('/login');
  }

  // Cloud & AI row values
  const cloudValue = addonSubscription
    ? 'Subscribed'
    : 'Not subscribed — $4.99/mo add-on';
  const handleCloudAdd = addonSubscription ? undefined : () => openExternalUrl(SCRIVAR_BILLING_URL);

  // Updates row subtitle
  const updatesSubtitle = version
    ? `Automatic · you're on the latest version`
    : 'Automatic';

  return (
    <div style={{ padding: '24px', maxWidth: '720px' }}>
      {/* View header */}
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 4px', color: 'var(--ds-ink, #191A24)' }}>
          Account &amp; settings
        </h2>
        <p style={{ fontSize: '13px', color: 'var(--ds-ink-2, #565869)', margin: 0 }}>
          Your profile, license and app preferences.
        </p>
      </div>

      {/* Profile row */}
      <ProfileRow
        name={name}
        email={email}
        onEditProfile={() => navigate('/account/profile')}
      />

      {/* Settings list */}
      <div style={{
        marginTop: '16px',
        border: '1px solid var(--ds-border, #EAEBF0)',
        borderRadius: 'var(--ds-radius, 12px)',
        boxShadow: 'var(--ds-shadow-card, 0 1px 2px rgba(20,22,48,.05))',
        overflow: 'hidden',
        background: 'var(--ds-surface, #fff)',
      }}>
        <SettingsItem
          icon={BadgeCheck}
          title="Plan"
          subtitle={planSubtitle(tier)}
          value="Manage"
          onRowClick={() => navigate('/account/billing')}
        />
        <SettingsItem
          icon={ShieldCheck}
          title="License &amp; devices"
          subtitle={hasLifetime ? 'This device activated' : 'No license on this device'}
          onRowClick={() => navigate('/account/devices')}
        />
        <SettingsItem
          icon={Cloud}
          title="Cloud &amp; AI"
          subtitle={cloudValue}
          value={addonSubscription ? undefined : 'Add'}
          valueColor="var(--ds-indigo, #4F46E5)"
          onValueClick={handleCloudAdd}
          onRowClick={addonSubscription ? () => navigate('/account/billing') : undefined}
        />
        <SettingsItem
          icon={Languages}
          title="Language"
          subtitle={<LanguageSwitcher />}
          onRowClick={undefined}
        />
        <SettingsItem
          icon={Moon}
          title="Appearance"
          subtitle={<ThemeSwitcher />}
          onRowClick={undefined}
        />
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          padding: '15px 20px',
        }}>
          {/* Updates row — last item, no border-bottom */}
          <div style={{
            width: '34px',
            height: '34px',
            borderRadius: '9px',
            background: 'var(--ds-sidebar, #F4F5F8)',
            color: 'var(--ds-ink-2, #565869)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <RefreshCw size={17} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h5 style={{ fontSize: '13.5px', fontWeight: 600, margin: '0 0 1px', color: 'var(--ds-ink, #191A24)' }}>
              Updates
            </h5>
            <p style={{ fontSize: '12px', color: 'var(--ds-ink-3, #8C8FA0)', margin: 0 }}>
              {updatesSubtitle}
            </p>
          </div>
          {version && (
            <div style={{
              fontSize: '13px',
              color: 'var(--ds-ink-3, #8C8FA0)',
              fontFamily: 'monospace',
            }}>
              {version}
            </div>
          )}
          <ChevronRight size={16} color="var(--ds-ink-4, #A9ABB8)" />
        </div>
      </div>

      {/* Sign out */}
      <button
        type="button"
        onClick={handleSignOut}
        style={{
          marginTop: '16px',
          height: '40px',
          padding: '0 18px',
          borderRadius: '9px',
          background: 'var(--ds-surface, #fff)',
          border: '1px solid var(--ds-border-2, #D4D7E3)',
          fontFamily: 'inherit',
          fontSize: '13px',
          fontWeight: 600,
          color: 'var(--ds-red, #B42318)',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <LogOut size={16} />
        Sign out
      </button>
    </div>
  );
}
